import axios from "axios";
import store, { persistor } from "../redux/store";
import { logout } from "../redux/userSlice";
import { apiConstants } from "../common/constants";

/**
 * Session teardown for the admin panel.
 *
 * Until the backend gained POST /api/auth/logout, signing out was purely local: the JWT stayed
 * valid server-side for the rest of its hour, so a token captured beforehand could be replayed
 * after logout (CWE-613, raised in the 2026 security audit). Telling the API is now the first
 * step of logging out, not an afterthought.
 *
 * Deliberately a plain module rather than a hook — logout is triggered from the account menu and
 * the axios interceptor alike, and the interceptor has no React context to hang a hook on.
 *
 * Mirrors proship-web/src/app/helpers/sessionHelper.js; keep the two in step.
 */

/** Bare client: the app-wide axios instance carries interceptors that would re-enter this file. */
const plainClient = axios.create();

/** Must match `key` in the store's persistConfig — redux-persist stores under `persist:<key>`. */
const PERSIST_STORAGE_KEY = "persist:root";

const REDIRECT_GUARD_KEY = "proship:sessionRedirectAt";
const REDIRECT_GUARD_MS = 5000;

let logoutInFlight = false;

/**
 * Wipe every trace of the session from the browser.
 *
 * The order here is load-bearing. redux-persist flushes asynchronously, but the callers below
 * tear the page down synchronously with a full page load — so simply dispatching the resets
 * leaves the PRE-logout blob sitting in localStorage. It then rehydrates on the next load,
 * `AppRoutes` sees `userInfo.accessToken` again, and the app signs itself back in with the token
 * we just revoked; every API call 401s, the interceptor redirects, and round it goes. That was a
 * genuine infinite reload loop, so: pause the persistor first (no further writes can be
 * scheduled), then clear state, then delete the stored blob by hand.
 *
 * The two dispatches are both needed. `logout()` clears the hand-managed localStorage keys,
 * `USER_LOGGED_OUT` resets the redux-persist tree. The Logout button used to dispatch only the
 * latter, which left userToken and the 12h refreshToken in localStorage after signing out.
 */
function clearLocalSession() {
  try {
    persistor.pause();
  } catch (e) {
    // Persistor unavailable — the explicit removeItem below is still the real guarantee.
  }

  store.dispatch(logout());
  store.dispatch({ type: "USER_LOGGED_OUT" });

  try {
    window.localStorage.removeItem(PERSIST_STORAGE_KEY);
  } catch (e) {
    console.error("Could not clear persisted session state", e);
  }
}

/** True if we bounced to login moments ago and are somehow back here already. */
function redirectLoopDetected() {
  try {
    const last = Number(window.sessionStorage.getItem(REDIRECT_GUARD_KEY)) || 0;
    return Date.now() - last < REDIRECT_GUARD_MS;
  } catch (e) {
    return false;
  }
}

function markRedirect() {
  try {
    window.sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now()));
  } catch (e) {
    /* sessionStorage unavailable — we simply lose the loop guard */
  }
}

function clearRedirectMark() {
  try {
    window.sessionStorage.removeItem(REDIRECT_GUARD_KEY);
  } catch (e) {
    /* nothing to clear */
  }
}

/**
 * Drop the dead session and send the user to login. For the involuntary path — a 401 telling us
 * the token is already revoked — where asking the API to revoke it again is pointless.
 *
 * Guarded against re-entry: if something is still handing out a dead token we stop redirecting
 * rather than reload forever. A broken page with a console error is recoverable; an infinite
 * reload locks the user out of the app entirely.
 */
export function clearSessionAndRedirect(redirectTo = "/") {
  clearLocalSession();

  if (redirectLoopDetected()) {
    console.error(
      "Session redirect loop detected — not reloading again. Clear site data for this origin and sign in."
    );
    return;
  }

  markRedirect();
  window.location.replace(redirectTo);
}

/**
 * User-initiated logout. Revokes the token server-side first, then clears locally.
 *
 * Local teardown happens in `finally`: if the API is unreachable the user must still end up
 * logged out of this browser. That leaves the token live server-side until it expires, which is
 * the best available outcome — refusing to log out because the network is down is worse.
 */
export async function performLogout(redirectTo = "/") {
  if (logoutInFlight) return;
  logoutInFlight = true;

  const token =
    store.getState()?.user?.userInfo?.accessToken || localStorage.getItem("userToken");

  try {
    if (token) {
      await plainClient.post(`${apiConstants.BASE_LOCAL}${apiConstants.LOGOUT}`, null, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
    }
  } catch (err) {
    console.error("Logout API call failed; clearing the session locally anyway", err);
  } finally {
    logoutInFlight = false;
    clearLocalSession();
    // A deliberate logout is never a loop — reset the guard so a later revocation can redirect.
    clearRedirectMark();
    window.location.replace(redirectTo);
  }
}
